import { useMemo } from 'react'
import { RefreshCw } from 'lucide-react'
import { useModal } from '@/components/modals'
import { Card, CardContent } from '@/components/ui/card'
import { EMPTY, ROW, TD, TH, relativeTime } from '@/features/fraud/format'
import { Spinner } from '@/features/fraud/shared'
import { discountErrorMessage } from '@/lib/api/discount-client'
import { useDiscountFallbackOrders, useDiscountLocations } from '@/lib/api/discount-queries'
import { useEvent } from '@/lib/api/queries'
import { formatInEventZone, timeZoneLabel } from '@/lib/eventTime'
import { cn } from '@/lib/utils'
import { locationLabel } from './editor-model'

const TIME_FORMAT = 'MMM d, h:mm:ss a'

/**
 * Offline POS orders whose claimed prices could not be checked against the
 * price book the device had, so the server re-priced them at the corrected
 * time. Staff check each customer was charged what they expected.
 */
export function FallbackTab({ eventId }: { eventId: number }) {
  const { openOrderModal } = useModal()
  const { data: event } = useEvent(eventId)
  const { data, isLoading, isFetching, error, dataUpdatedAt, refetch } = useDiscountFallbackOrders(eventId)
  // Names the location of each order; one it can't name shows its id.
  const { data: locationsData } = useDiscountLocations(eventId)
  const timeZone = event?.timezone

  const locations = useMemo(
    () => new Map((locationsData ?? []).map((location) => [location.id, location])),
    [locationsData],
  )
  const orders = data?.orders ?? []
  const when = (iso: string | null) => (iso ? formatInEventZone(iso, timeZone, TIME_FORMAT) : EMPTY)

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="text-sm text-neutral-600 dark:text-neutral-400 max-w-3xl">
          Offline POS orders whose prices couldn't be checked against the price book the device had, so they were
          re-priced at the corrected order time. Check each customer was charged what they expected.
        </p>
        <div className="flex items-center gap-3 text-xs text-neutral-500 dark:text-neutral-400">
          {dataUpdatedAt ? <span>Updated {relativeTime(new Date(dataUpdatedAt).toISOString())}</span> : null}
          <button onClick={() => refetch()} className="inline-flex items-center gap-1 hover:text-neutral-900 dark:hover:text-white" disabled={isFetching}>
            <RefreshCw className={cn('w-3.5 h-3.5', isFetching && 'animate-spin')} /> Refresh
          </button>
        </div>
      </div>

      {error ? (
        <p className="text-sm text-red-600 dark:text-red-400">Failed to load orders that need review: {discountErrorMessage(error)}</p>
      ) : isLoading || !data ? (
        <div className="flex items-center justify-center py-16"><Spinner /></div>
      ) : orders.length === 0 ? (
        <div className="rounded-lg border border-dashed border-neutral-300 dark:border-neutral-700 px-4 py-12 text-center text-sm text-neutral-500 dark:text-neutral-400">
          Nothing needs review
        </div>
      ) : (
        <Card>
          <CardContent className="overflow-x-auto px-0 pt-2">
            <table className="w-full whitespace-nowrap">
              <thead>
                <tr className="border-b border-neutral-200 dark:border-neutral-800">
                  <th className={cn(TH, 'pl-6')}>Order</th>
                  <th className={TH} title="When the server received the order">Created</th>
                  <th className={TH} title="The order time the prices were taken at: the device's order time, corrected for its clock">Priced at</th>
                  <th className={TH} title="The device clock when the order was taken">Device created</th>
                  <th className={TH} title="The device clock when the order was sent">Device sent</th>
                  <th className={cn(TH, 'pr-6')}>Location</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => {
                  const location = order.vendor_event_id != null ? locations.get(order.vendor_event_id) : undefined
                  return (
                    <tr key={order.id} className={ROW}>
                      <td className={cn(TD, 'pl-6')}>
                        <button
                          type="button"
                          onClick={() => openOrderModal(order.id, eventId)}
                          className="font-medium text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          {order.order_display_id || `#${order.id}`}
                        </button>
                      </td>
                      <td className={TD}>{when(order.created_at)}</td>
                      <td className={cn(TD, 'font-medium text-neutral-900 dark:text-white')}>{when(order.priced_at)}</td>
                      <td className={TD}>{when(order.device_created_at)}</td>
                      <td className={TD}>{when(order.device_sent_at)}</td>
                      <td className={cn(TD, 'pr-6 whitespace-normal')}>
                        {location ? (
                          locationLabel(location)
                        ) : order.vendor_event_id != null ? (
                          `Location #${order.vendor_event_id}`
                        ) : (
                          EMPTY
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </CardContent>
          <p className="px-6 pb-4 pt-1 text-xs text-neutral-500 dark:text-neutral-400">
            Times in {timeZone ? timeZoneLabel(timeZone) : 'your local time'}.
            {orders.length >= 200 ? ' Showing the newest 200 orders.' : ''}
          </p>
        </Card>
      )}
    </div>
  )
}
